/* ═══════════════════════════════════════════════════════════════════════════
   ARTBERY — BARRE FLOTTANTE « TRIER / FILTRE » · script partagé
   ═══════════════════════════════════════════════════════════════════════════

   DEUX CONSOMMATEURS : la maquette autonome, et la section Shopify
   artbery-collection-barre. Une correction faite ici est donc déjà dans le
   composant Shopify.

   CE QU'IL FAIT — trois choses, et rien de plus :
     · allumer le bouton ouvert (le blanc plein du design system), et faire
       passer le bouton de tri de sa flèche à une croix pendant que son menu
       est ouvert ;
     · placer le menu de tri de Prestige juste au-dessus du bouton rond, au
       lieu du haut de l'écran où l'ancrait la barre native ;
     · décider QUAND la barre apparaît, et quand elle se transforme en
       « Retour en haut ».

   CE QU'IL NE FAIT PAS, ET NE FERA JAMAIS : filtrer ou trier. Sur le site, les
   deux boutons portent un attribut `aria-controls` qui désigne le tiroir de
   filtres et le menu de tri DE PRESTIGE. C'est Prestige qui les ouvre, qui
   connaît les facettes et qui recharge la grille. Nous ne faisons que peindre
   le bouton et dire où le panneau se pose.

   D'OÙ LES DEUX RÉGIMES d'allumage, choisis tout seuls :
     · si les boutons ont un `aria-controls`, c'est Prestige qui tient l'état,
       sur l'attribut `aria-expanded` — on le recopie. Sans ça, un panneau fermé
       d'un clic à l'extérieur laisserait le bouton allumé, à mentir ;
     · sinon (la maquette, où aucun panneau n'existe), on gère l'allumage
       nous-mêmes : un seul ouvert à la fois, recliquer le même remet au repos.

   ⚠️ LA GRILLE DE PRODUITS EST REMPLACÉE À CHAQUE FILTRAGE, et c'est le piège
   central de ce fichier. Prestige ne recharge pas la page : il va chercher la
   section rendue et fait `replaceChildren` sur elle. La grille qu'on observait
   se retrouve donc détachée du document — et un IntersectionObserver, sur une
   cible détachée, signale un rectangle entièrement à zéro. « bas ≤ 0 » devient
   vrai, la barre croit que toute la grille est passée au-dessus de l'écran et
   se fige sur « Retour en haut », sans plus jamais rien recevoir. C'est
   exactement le bug observé. D'où les deux gardes ci-dessous : on ignore ce que
   dit une cible détachée, et on se rebranche sur la nouvelle grille dès qu'elle
   arrive.

   ROBUSTESSE — tout est sous try/catch et vérifie la présence de ses éléments :
   le script peut être chargé sur une page sans barre, et une erreur ici ne doit
   jamais empêcher le reste de fonctionner.
   ═══════════════════════════════════════════════════════════════════════════ */

(function(){
  'use strict';

  /* ── LE MENU DE TRI : OÙ IL S'OUVRE ────────────────────────────────────────
     `sort-by-popover` est l'identifiant écrit en dur par Prestige dans
     sections/main-collection.liquid — il ne change pas d'une collection à
     l'autre. Dans la maquette il n'existe pas : tout ce bloc s'abstient alors
     de lui-même, sans rien casser. */

  function menuTri(){ return document.getElementById('sort-by-popover'); }
  function boutonTri(){ return document.querySelector('.ab-cbar__sort'); }

  /* On écrit deux coordonnées sur le panneau, le CSS fait le reste (voir le
     pavé « LE MENU DE TRI DE PRESTIGE, RAMENÉ AU-DESSUS » dans
     artbery-barre.css). Mesuré au moment de l'ouverture : la barre est fixe,
     sa position ne bouge donc qu'au redimensionnement, et le panneau démarre
     son apparition à opacité nulle — on écrit avant qu'il se voie. */
  function placerMenuTri(){
    try{
      var menu = menuTri(), btn = boutonTri();
      if(!menu || !btn) return;
      var r = btn.getBoundingClientRect();
      if(!r.width) return;                      /* bouton masqué : rien à ancrer */
      menu.style.setProperty('--ab-tri-gauche', Math.round(r.left) + 'px');
      menu.style.setProperty('--ab-tri-bas', Math.round(window.innerHeight - r.top) + 'px');
    }catch(e){}
  }

  function fermerMenuTri(){
    try{
      var menu = menuTri();
      if(!menu || !menu.hasAttribute('open')) return;
      /* `hide()` est la méthode publique du panneau : elle joue l'animation de
         sortie et rend le focus. Le retrait d'attribut n'est qu'un filet, pour
         le cas où le composant ne serait pas encore initialisé. */
      if(typeof menu.hide === 'function'){ menu.hide(); } else { menu.removeAttribute('open'); }
    }catch(e){}
  }

  /* ── ALLUMAGE DES BOUTONS ──────────────────────────────────────────────── */

  /* Un seul endroit peint un bouton, quel que soit le régime : la classe sur le
     bouton, la même classe sur son segment (pour que la bordure passe au blanc
     avec lui), et l'intitulé lu par les lecteurs d'écran — qui doit dire ce que
     le prochain clic va faire, pas ce qui est déjà à l'écran. */
  function peindre(it, ouvert){
    it.classList.toggle('is-open', ouvert);
    var seg = it.closest ? it.closest('.ab-cbar__seg') : null;
    if(seg) seg.classList.toggle('is-open', ouvert);
    if(it.classList.contains('ab-cbar__sort')){
      it.setAttribute('aria-label', ouvert ? 'Fermer le tri' : 'Trier');
      if(ouvert) placerMenuTri();
    }
  }

  /* LE RACCORD APRÈS UN REMPLACEMENT DE SECTION. Nos boutons ne s'allument que
     parce que le panneau leur pose `aria-expanded` en s'ouvrant et en se
     fermant. Or un filtrage détruit le panneau et en installe un neuf, fermé :
     si l'ancien était ouvert à cet instant, personne ne vient jamais éteindre
     le bouton, qui reste blanc — croix affichée — devant un menu qui n'existe
     plus. On le raccorde donc à la seule vérité disponible, l'attribut `open`
     du panneau lui-même. */
  function resynchroniser(){
    try{
      var items = document.querySelectorAll('[data-cbar-item][aria-controls]');
      [].forEach.call(items, function(it){
        var panneau = document.getElementById(it.getAttribute('aria-controls'));
        var ouvert = !!(panneau && panneau.hasAttribute('open'));
        it.setAttribute('aria-expanded', ouvert ? 'true' : 'false');
        peindre(it, ouvert);
      });
    }catch(e){}
  }

  try{
    var barres = document.querySelectorAll('[data-cbar]');

    [].forEach.call(barres, function(bar){
      var items = [].slice.call(bar.querySelectorAll('[data-cbar-item]'));
      if(!items.length) return;

      var pilote = items.some(function(it){ return it.hasAttribute('aria-controls'); });

      if(pilote){
        /* Régime « le panneau décide » : on suit aria-expanded. */
        if(!('MutationObserver' in window)) return;
        items.forEach(function(it){
          new MutationObserver(function(){
            peindre(it, it.getAttribute('aria-expanded') === 'true');
          }).observe(it, { attributes:true, attributeFilter:['aria-expanded'] });
        });
        return;
      }

      /* Régime « maquette » : rien derrière, on peint nous-mêmes. */
      items.forEach(function(it){
        it.addEventListener('click', function(){
          var etaitOuvert = it.classList.contains('is-open');
          items.forEach(function(o){ peindre(o, false); o.setAttribute('aria-pressed','false'); });
          if(!etaitOuvert){ peindre(it, true); it.setAttribute('aria-pressed','true'); }
        });
      });
    });
  }catch(e){}

  /* ── QUAND LA BARRE APPARAÎT, QUAND ELLE SE TRANSFORME ─────────────────── */
  try{
    var wrap = document.querySelector('[data-floating-cbar]');
    if(!wrap) return;

    var topBtn = wrap.querySelector('[data-scroll-top]');
    if(!topBtn) return;

    topBtn.addEventListener('click', function(){
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });

    /* La grille : celle de la maquette, ou celle de Prestige. Un attribut
       explicite l'emporte sur les deux, pour le jour où elle change de nom.
       Relu à chaque filtrage, puisque ce n'est plus le même élément. */
    function trouverGrille(){
      return document.querySelector('[data-cbar-grille]')
          || document.querySelector('.product-grid .grid')
          || document.querySelector('.product-list');
    }

    var grille = trouverGrille();
    if(!grille) return;

    /* Le conteneur de section, retenu UNE SEULE FOIS : lui survit au filtrage,
       la grille non. C'est notre point d'observation stable. Nul dans la
       maquette, qui n'a pas de sections Shopify — le bloc de rebranchement
       s'abstient alors tout seul. */
    var section = grille.closest ? grille.closest('.shopify-section') : null;

    /* Deux observateurs écrivent dans le même état, un seul endroit décide de
       l'apparence : évite que l'un rallume ce que l'autre vient d'éteindre. */
    var st = { atteint:false, depassee:false };
    function appliquer(){
      if(st.depassee){                       /* toute la grille est passée au-dessus */
        /* Le bouton de tri disparaît en même temps que ce mode s'installe : on
           referme son menu, sinon il resterait ouvert, accroché à un bouton
           qui n'est plus là. */
        fermerMenuTri();
        wrap.classList.add('is-visible');
        wrap.classList.add('is-top-mode');
      } else if(st.atteint){                 /* dans la grille, 2e ligne atteinte */
        wrap.classList.add('is-visible');
        wrap.classList.remove('is-top-mode');
      } else {                               /* pas encore descendu jusque-là */
        wrap.classList.remove('is-visible');
        wrap.classList.remove('is-top-mode');
      }
    }

    if(!('IntersectionObserver' in window)){ wrap.classList.add('is-visible'); return; }

    /* Un observateur continue de parler après que sa cible a quitté le
       document, et il ne dit alors que des zéros. Sans ce garde, ces zéros
       seraient lus comme « la grille est passée au-dessus de l'écran ». */
    function vivante(entree){
      return entree.target && entree.target.isConnected !== false;
    }

    /* La barre apparaît à la première carte de la DEUXIÈME ligne — pas au
       premier pixel de la grille, ce qui arrivait bien trop tôt.
       Le nombre de colonnes change (2 sur mobile, 4 sur ordinateur), il n'est
       donc pas codé en dur : on prend la première carte dont le haut est plus
       bas que celui de la première carte — c'est la définition même d'un
       changement de ligne, quel que soit le nombre de colonnes.
       Certaines collections n'auront qu'une seule ligne de produits — et un
       filtre serré en laisse souvent une seule : on retombe alors sur la
       dernière carte de cette ligne. La barre apparaît un peu plus tard, mais
       elle apparaît. Aucune page ne s'en retrouve privée.
       Les deux sélecteurs de carte : `.card` dans la maquette, `.product-card`
       dans le thème. */
    function elementDeclencheur(){
      var cards = grille.querySelectorAll('.card, .product-card');
      if(!cards.length) return grille;
      var haut0 = cards[0].offsetTop;
      for(var i = 1; i < cards.length; i++){
        if(cards[i].offsetTop > haut0 + 1) return cards[i];   /* 1px de tolérance */
      }
      return cards[cards.length - 1];                         /* une seule ligne */
    }

    /* `top < 0` = la ligne déclencheuse est sortie par le haut : on est plus
       bas dans la grille, la barre doit rester. Sans ce test elle disparaîtrait
       dès que la deuxième ligne quitte l'écran. */
    var showIO = null, cible = null;
    function brancherApparition(){
      var el = elementDeclencheur();
      if(el === cible) return;
      if(showIO) showIO.disconnect();
      cible = el;
      showIO = new IntersectionObserver(function(entries){
        var e = entries[0];
        if(!vivante(e)) return;
        st.atteint = e.isIntersecting || e.boundingClientRect.top < 0;
        appliquer();
      }, { threshold: 0 });
      showIO.observe(el);
    }

    /* La grille entière ne sert qu'au mode « Retour en haut » : il se déclenche
       quand elle est entièrement passée au-dessus de l'écran — donc seulement
       s'il y a du contenu en dessous (le pied de page du site, par exemple). */
    var grilleIO = null;
    function brancherGrille(){
      if(grilleIO) grilleIO.disconnect();
      grilleIO = new IntersectionObserver(function(entries){
        var e = entries[0];
        if(!vivante(e)) return;
        st.depassee = !e.isIntersecting && e.boundingClientRect.bottom <= 0;
        appliquer();
      }, { threshold: 0 });
      grilleIO.observe(grille);
    }

    function brancher(){
      brancherGrille();
      cible = null;              /* la carte déclencheuse d'avant n'existe plus */
      brancherApparition();
    }
    brancher();

    /* LE REBRANCHEMENT APRÈS UN FILTRAGE OU UN TRI. Prestige remplace tous les
       enfants de la section d'un coup : on surveille exactement ça, sur le seul
       élément qui survit. Dès qu'une autre grille est en place, les deux
       observateurs repartent sur elle et redisent la vérité — c'est ce qui
       débloque « Retour en haut ». */
    if(section && ('MutationObserver' in window)){
      new MutationObserver(function(){
        var g = trouverGrille();
        if(!g || g === grille) return;
        grille = g;
        brancher();
        resynchroniser();      /* les panneaux aussi sont neufs : voir plus haut */
      }).observe(section, { childList:true });
    }

    /* Le passage 2 ↔ 4 colonnes déplace la deuxième ligne : on recalcule la
       cible au redimensionnement, sinon la barre garderait le déclencheur de
       l'autre format. La barre change de largeur du même coup : si le menu de
       tri est ouvert, il doit suivre son bouton. */
    var t;
    window.addEventListener('resize', function(){
      clearTimeout(t);
      t = setTimeout(function(){
        brancherApparition();
        var menu = menuTri();
        if(menu && menu.hasAttribute('open')) placerMenuTri();
      }, 150);
    });
  }catch(e){}
})();
